'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useEffect, useId, useState, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Field, Input } from '@kiao-voice/ui-kit'
import {
  ANALYTICS_EVENTS,
  isOnAnalyticsExcludedPath,
  trackEvent,
  trackLogin,
} from '@/lib/analytics'
import { OtpApiError, requestOtp, verifyOtp, type OtpVerifyResponse } from './auth-storage'
import type { OtpPurpose } from '@kiao-voice/core'

type Step = 'email' | 'code'

/**
 * Which surface captured the signup — the `source` on the signup funnel events.
 * The vocabulary is wider than this component: `anonymous_result` belongs to the
 * inline form on the trial result page, which does not mount this form and
 * reports `signup_email_submitted` as well as `signup_completed`. From HERE only
 * `signup_completed` is ever reported, because none of the surfaces that mount
 * this form is the moment a lead comes into existence.
 */
export type SignupSource =
  | 'auth_modal'
  | 'team_invite'
  | 'promotion_invite'
  | 'anonymous_result'
  // `/desktop-login` — the browser half of desktop sign-in. Its own value
  // because a signup that arrived through the desktop app is a different
  // acquisition story from one that started on the marketing site, and folding
  // it into `auth_modal` would make that invisible.
  | 'desktop_login'
  // The survey page's in-place sign-in (`features/survey-respond/inline-login.tsx`):
  // someone who signs up to answer a survey arrived through research outreach,
  // not the marketing site.
  | 'survey'

/** design/js/kiao-login.js COOLDOWN — resend lockout after each send. */
const RESEND_COOLDOWN_SECONDS = 60

interface OtpFormProps {
  purpose: OtpPurpose
  /** Optional headline above the form. Omit when the host renders its own
   *  heading (the kiao-login modal puts it in the `.km-head`). */
  title?: string
  /** Helper copy under the headline. */
  subtitle?: string
  /** Pre-fills the email field (e.g. the invited address from a redeem). */
  initialEmail?: string
  /** Locks the email to {@link initialEmail}: renders it read-only and hides
   *  the "use a different email" action. Used by the invite sign-in step so the
   *  user can only authenticate as the invited address. */
  lockEmail?: boolean
  /** Rule copy under the email field — e.g. why a locked address cannot change.
   *  Rendered as the Field hint so it is wired to the input's description. */
  emailHint?: React.ReactNode
  /** Sends the code for {@link initialEmail} on mount and opens directly on the
   *  code step. For hosts that already collected the address (the campaign
   *  banner), where re-asking for it would be a pointless second prompt. A
   *  failed send falls back to the email step with the error shown, so the user
   *  is never stranded on a code step for a code that was never sent. */
  autoSend?: boolean
  /** Submit-button label on the email step. */
  emailCtaLabel?: string
  /** Submit-button label on the code step. */
  codeCtaLabel?: string
  /** Slot below the form (e.g. "Already have an account? Sign in"). */
  footer?: React.ReactNode
  /** Called with the verify response once the OTP succeeds. */
  onVerified: (result: OtpVerifyResponse) => void
  /** Extra fields merged into the verify payload — e.g. a referral code on the
   *  signup flow (issue #71). Kept generic so login/invite hosts pass nothing. */
  extraVerifyPayload?: { referralCode?: string }
  /**
   * Reported as `source` on the `signup_completed` event. Required rather than
   * defaulted: four different surfaces mount this form, and a default would
   * quietly file three of them under the fourth's name.
   */
  analyticsSource: SignupSource
  /** Test seam — defaults to the real /auth/otp/request call. */
  requestFn?: typeof requestOtp
  /** Test seam — defaults to the real /auth/otp/verify call. */
  verifyFn?: typeof verifyOtp
}

/**
 * Two-step email-then-code form, styled per design/js/kiao-login.js
 * (`.kiao-login` mono modal: `.field` + `.alert` errors + `.btn-block`
 * CTA + `.auth-aux` resend/change row + `.toast` resend confirmation).
 *
 * The "email" step posts to /auth/otp/request, which always returns 202
 * (preventing enumeration) — so reaching the code step is NOT proof the
 * email is registered; that's by design.
 */
export function OtpForm({
  purpose,
  title,
  subtitle,
  emailCtaLabel,
  codeCtaLabel,
  footer,
  initialEmail,
  lockEmail = false,
  emailHint,
  autoSend = false,
  onVerified,
  extraVerifyPayload,
  analyticsSource,
  requestFn = requestOtp,
  verifyFn = verifyOtp,
}: OtpFormProps) {
  const { t } = useTranslation('web')
  const emailInputId = useId()
  const codeInputId = useId()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** Shows the `.toast` "已重新寄送驗證碼。" after a successful resend. */
  const [resent, setResent] = useState(false)
  /** Seconds left on the resend cooldown; 0 = resend available. */
  const [cooldown, setCooldown] = useState(0)
  const [isPending, startTransition] = useTransition()

  // 1-second countdown — mirrors the prototype's setInterval. Keyed on
  // the active flag (not the second value) so one interval drives the
  // whole countdown instead of re-arming per tick.
  const cooldownActive = cooldown > 0
  useEffect(() => {
    if (!cooldownActive) return
    const id = window.setInterval(() => {
      setCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [cooldownActive])

  const sendLabel = emailCtaLabel ?? t('auth.ctaSendCode')
  const verifyLabel = codeCtaLabel ?? t('auth.ctaSignupVerify')

  const sendCode = React.useCallback(
    (target: string) => {
      setError(null)
      const trimmed = target.trim().toLowerCase()
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setError(t('auth.errorInvalidEmail'))
        setStep('email')
        return
      }
      setEmail(trimmed)

      startTransition(async () => {
        try {
          const session = await requestFn(trimmed, purpose)
          if (session) {
            onVerified(session)
            return
          }
          setStep('code')
          setCode('')
          setResent(false)
          setCooldown(RESEND_COOLDOWN_SECONDS)
        } catch (cause) {
          reportHandledException(cause, { source: 'features/auth/otp-form.tsx' })
          // Any failure returns to the email step: a code step with no code in
          // flight is a dead end the user cannot reason about.
          setStep('email')
          if (cause instanceof OtpApiError && cause.status === 429) {
            setError(t('auth.errorRateLimited'))
            return
          }
          setError(cause instanceof Error ? cause.message : t('auth.errorRequestFailed'))
        }
      })
    },
    [onVerified, purpose, requestFn, t]
  )

  // Auto-send runs once per mount. The host unmounts this form when its modal
  // closes, so reopening deliberately sends a fresh code rather than resuming a
  // stale one.
  const autoSentRef = React.useRef(false)
  useEffect(() => {
    if (!autoSend || autoSentRef.current) return
    if (!initialEmail) return
    autoSentRef.current = true
    sendCode(initialEmail)
  }, [autoSend, initialEmail, sendCode])

  const submitEmail = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    sendCode(email)
  }

  const submitCode = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    const trimmedCode = code.trim()
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError(t('auth.errorCodeLength'))
      return
    }

    startTransition(async () => {
      try {
        const result = await verifyFn({ email, code: trimmedCode, purpose, ...extraVerifyPayload })
        // Only when the API says this call is what created the account. The
        // flow is unified — the same form signs an existing user in — so
        // reporting every verify would count returning visitors as signups.
        // Same dispatch-time check as the request step above. This form is
        // mounted on `/promotion/[token]` and `/invite/[token]`, whose paths ARE
        // the secret they carry.
        if (!isOnAnalyticsExcludedPath()) {
          if (result.accountWasCreated === true) {
            trackEvent(ANALYTICS_EVENTS.signupCompleted, { source: analyticsSource })
          } else if (result.accountWasCreated === false) {
            // An existing account signed in. Unknown (the API did not say)
            // reports neither: a guess cannot be told apart from a fact later.
            trackLogin(analyticsSource)
          }
        }
        onVerified(result)
      } catch (cause) {
        reportHandledException(cause, { source: 'features/auth/otp-form.tsx' })
        if (cause instanceof OtpApiError && cause.status === 401) {
          setError(reasonToMessage(cause.reason, t))
          return
        }
        setError(cause instanceof Error ? cause.message : t('auth.errorVerifyFailed'))
      }
    })
  }

  const resend = () => {
    if (cooldown > 0) return
    setError(null)
    setResent(false)
    // Prototype starts the cooldown on click, before the request settles.
    setCooldown(RESEND_COOLDOWN_SECONDS)
    startTransition(async () => {
      try {
        await requestFn(email, purpose)
        setResent(true)
      } catch (cause) {
        reportHandledException(cause, { source: 'features/auth/otp-form.tsx' })
        if (cause instanceof OtpApiError && cause.status === 429) {
          setError(t('auth.errorResendRateLimited'))
          return
        }
        setError(cause instanceof Error ? cause.message : t('auth.errorRequestFailed'))
      }
    })
  }

  const changeEmail = () => {
    setStep('email')
    setCode('')
    setError(null)
    setResent(false)
    setCooldown(0)
  }

  return (
    <>
      {title ? (
        <header style={{ marginBottom: 14 }}>
          <h3>{title}</h3>
          {subtitle ? <p className="note">{subtitle}</p> : null}
        </header>
      ) : null}

      {/* noValidate（兩步皆同）：錯誤一律走 .alert（prototype 自訂驗證），
          不觸發瀏覽器原生驗證泡泡 */}
      {step === 'email' ? (
        <form className="step show otp-form-step" noValidate onSubmit={submitEmail}>
          <Field label={t('auth.emailLabel')} htmlFor={emailInputId} hint={emailHint}>
            <Input
              id={emailInputId}
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              autoFocus
              readOnly={lockEmail}
              value={email}
              onChange={(event) => {
                if (lockEmail) return
                setEmail(event.target.value)
                setError(null)
              }}
            />
          </Field>
          {error ? <Alert>{error}</Alert> : null}
          <Button type="submit" className="btn-block" disabled={isPending}>
            {isPending ? t('auth.ctaSending') : sendLabel}
          </Button>
        </form>
      ) : (
        <form className="step show otp-form-step" noValidate onSubmit={submitCode}>
          <p className="note" role="status">
            {t('auth.codeSent', { email })}
          </p>
          <Field label={t('auth.codeLabel')} htmlFor={codeInputId}>
            <Input
              id={codeInputId}
              className="code-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="······"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                setError(null)
              }}
            />
          </Field>
          {error ? <Alert>{error}</Alert> : null}
          <Button type="submit" className="btn-block" disabled={isPending}>
            {isPending ? t('auth.ctaVerifying') : verifyLabel}
          </Button>
          <div className="auth-aux">
            <Button variant="ghost" size="sm" disabled={cooldown > 0 || isPending} onClick={resend}>
              {cooldown > 0
                ? t('auth.resendCooldown', { seconds: cooldown })
                : t('auth.resendCode')}
            </Button>
            {lockEmail ? null : (
              <Button variant="ghost" size="sm" onClick={changeEmail}>
                {t('auth.useDifferentEmail')}
              </Button>
            )}
          </div>
          {resent ? (
            <p className="toast show" role="status">
              {t('auth.newCodeSent')}
            </p>
          ) : null}
        </form>
      )}

      {footer ? <div className="note">{footer}</div> : null}
    </>
  )
}

function reasonToMessage(reason: string | undefined, t: (key: string) => string): string {
  switch (reason) {
    case 'expired':
      return t('auth.errorCodeExpired')
    case 'too_many_attempts':
      return t('auth.errorCodeLockout')
    case 'not_found':
    case 'invalid':
      return t('auth.errorCodeIncorrect')
    default:
      return t('auth.errorCodeFallback')
  }
}
